/**
 * UTM / tracking params preservation across navigation.
 *
 * Captures UTM params (+ fbclid, gclid, ref, variante) from the URL on first
 * load and persists them in localStorage. Provides helpers to append stored
 * params to any internal navigation URL.
 */

const STORAGE_KEY = "welltrainer_utm"

const TRACKED_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "fbclid",
  "gclid",
  // Google Ads em iOS 14.5+ nao manda gclid: manda gbraid (web -> app) ou
  // wbraid (app -> web). gad_source / gad_campaignid sao os "session
  // attributes", caminho reserva de atribuicao no Data Manager do Google.
  "gbraid",
  "wbraid",
  "gad_source",
  "gad_campaignid",
  // Meta: `cid` e `aid` sao os ids da campanha e do anuncio, `plac` e onde o
  // anuncio apareceu e `site` diz se veio do Facebook ou do Instagram. Os ids
  // importam porque NOME de campanha muda, e cada mudanca vira um balde novo no
  // relatorio; o id nao muda nunca. Guardados aqui para sobreviverem ao pulo
  // pela landing, do mesmo jeito que o fbclid ja sobrevive.
  "cid",
  "aid",
  "plac",
  "site",
  "ref",
  "variante",
  "v",
  "s",
] as const

/**
 * Cookies `wt_*` no dominio raiz (.welltrainer.com.br), gravados no primeiro
 * toque pelo mu-plugin `wt-utm-tudo.php` da landing.
 *
 * Eles sao a PONTE entre welltrainer.com.br e app.welltrainer.com.br: como o
 * app e um subdominio, o navegador manda esses cookies para ca sozinho, mesmo
 * quando a pessoa chegou por um link sem parametro nenhum — fechou a aba e
 * voltou dias depois, abriu por outro dispositivo do mesmo navegador, digitou o
 * endereco. Sem eles, a campanha so existia enquanto a query string existisse.
 */
const COOKIE_FALLBACK_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "fbclid",
  "gclid",
  "gbraid",
  "wbraid",
  "gad_source",
  "gad_campaignid",
  "cid",
  "aid",
  "plac",
  "site",
  "pagina_de_entrada",
  "referrer_externo",
] as const

function lerCookieWt(nome: string): string | undefined {
  if (typeof document === "undefined") return undefined
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)wt_${nome}=([^;]*)`))
  if (!match) return undefined
  try {
    return decodeURIComponent(match[1]) || undefined
  } catch {
    return undefined
  }
}

/**
 * Sanitiza um `utm_source` contaminado pelo script de terceiros UTMify, que
 * cola um session-id no final do valor real.
 *
 * Padrão observado em produção (2026-05):
 *   "organicjLj69e95308a3f7d0879d5c522c"
 *   "lp-ptjLj6a067076ecce3d24c024547b"
 *   "chatgpt.comjLj6a06393f81e7acd63703c2fe"
 *
 * O separador é literalmente "jLj" (com J e L em maiúscula, único pra essa
 * lib) seguido de 16+ caracteres hex/base62. A heurística antiga só pegava
 * fontes "conhecidas" (organic/direct/referral/social) e deixava passar
 * fontes legítimas como `lp-pt`, `chatgpt.com`, etc.
 *
 * Agora: detecta o marker `jLj` + 16+ alfanuméricos em qualquer posição,
 * preserva tudo antes dele. Se não houver marker, devolve o valor original.
 */
const UTMIFY_MARKER_REGEX = /jLj[A-Za-z0-9]{16,}$/

/**
 * Origens que só dizem "não sei de onde", e por isso não são origem.
 *
 * O `organic` é o que a UTMify escrevia em quem chegava sem campanha, e os links
 * dela continuam circulando depois que ela saiu (02/09/2026). Lido como origem,
 * ele contava como campanha nova e passava por cima da que estava guardada, e o
 * servidor gravava "organic", que não é canal nenhum. Espelha `NAO_E_ORIGEM` de
 * `api/src/helpers/sanitizar-utm-source.ts`: a lista é a mesma nos dois lados.
 */
const NAO_E_ORIGEM = new Set([
  "organic",
  "direct",
  "(direct)",
  "(none)",
  "none",
  "(not set)",
  "not set",
  "lp",
  "lp-pt",
  "lp-gg",
  "null",
  "undefined",
])

export function limparUtmSource(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined
  const trimmed = String(raw).trim()
  if (!trimmed) return undefined
  const limpo = trimmed.replace(UTMIFY_MARKER_REGEX, "")
  const final = limpo.trim()
  if (!final || NAO_E_ORIGEM.has(final.toLowerCase())) return undefined
  return final
}

/**
 * O id de sessão da UTMify: 24 hexadecimais, o formato de um ObjectId.
 *
 * O sufixo depois do `jLj` era tirado como "o fbclid que a UTMify colou no
 * utm_source", aqui e no `captureFbclid`, e é o id de sessão dela: 230 contas
 * ficaram com ele no lugar do fbclid (medido em 27/10/2026), e dele saía um
 * `_fbc` inventado para o Meta e a dedução de `facebook / cpc` de um visitante
 * sem campanha. O fbclid do Meta nunca é só hexadecimal minúsculo. Espelha
 * `fbclidDeVerdade` de `api/src/helpers/sanitizar-utm-source.ts`.
 */
const ID_DE_SESSAO_DA_UTMIFY = /^[0-9a-f]{24}$/

/** O fbclid, quando ele é um fbclid; o id de sessão da UTMify vira `undefined`. */
export function fbclidDeVerdade(raw: string | null | undefined): string | undefined {
  const limpo = (raw ?? "").trim()
  if (!limpo || ID_DE_SESSAO_DA_UTMIFY.test(limpo)) return undefined
  return limpo
}

/** O `_fbc` (`fb.1.<instante>.<fbclid>`), quando o clique dentro dele é de verdade. */
export function fbcDeVerdade(raw: string | null | undefined): string | undefined {
  const limpo = (raw ?? "").trim()
  if (!limpo) return undefined
  const clique = limpo.split(".").slice(3).join(".")
  return clique && !fbclidDeVerdade(clique) ? undefined : limpo
}

/**
 * O QUE A VERSÃO ANTIGA GUARDOU COM O ID DA UTMIFY, limpo na leitura.
 *
 * Até 27/10/2026 o id virava fbclid no aparelho, e numa página seguinte sem
 * campanha na URL o `captureUtmParams` deduzia `facebook / cpc` dele. O fbclid
 * sai, e a dedução sai junto quando foi só ele que a sustentou: anúncio do Meta
 * de verdade traz fbclid de verdade, e link etiquetado traz campanha.
 */
function semFbclidDaUtmify(guardado: Record<string, string>): Record<string, string> {
  if (!guardado.fbclid || fbclidDeVerdade(guardado.fbclid)) return guardado
  const limpo = { ...guardado }
  delete limpo.fbclid
  const deduzido =
    limpo.utm_source === "facebook" &&
    (!limpo.utm_medium || limpo.utm_medium === "cpc") &&
    !limpo.utm_campaign &&
    !limpo.utm_content &&
    !limpo.utm_term
  if (deduzido) {
    delete limpo.utm_source
    delete limpo.utm_medium
  }
  return limpo
}

/**
 * Capture current URL's tracking params into localStorage.
 * Call once on app entry (e.g. welcome/landing page).
 * Only overwrites if the current URL actually has tracking params, otherwise
 * keeps whatever was previously stored.
 */
export function captureUtmParams(): void {
  if (typeof window === "undefined") return

  const url = new URL(window.location.href)
  const params: Record<string, string> = {}

  for (const key of TRACKED_KEYS) {
    const val = url.searchParams.get(key)
    if (val) params[key] = val
  }

  /* A ORIGEM É LIMPA ANTES DE DECIDIR SE A URL TROUXE CAMPANHA.
     Limpa depois, o `organic` da UTMify contava como campanha: os cookies da
     landing (a origem de verdade) eram pulados e o que se gravava por cima era
     uma origem vazia. Ver `limparUtmSource`.

     O sufixo depois do `jLj` NÃO é fbclid. Ele era tirado daqui como "o fbclid
     que a UTMify colou", e é o id de sessão dela (24 hexadecimais): 230 contas
     ficaram com esse id no lugar do fbclid, e dele saía um `fbc` inventado para o
     Meta (medido em 27/10/2026). Com a origem limpa, ele ainda viraria
     "facebook / cpc" logo abaixo. */
  if (params.utm_source) {
    const limpo = limparUtmSource(params.utm_source)
    if (limpo) params.utm_source = limpo
    else delete params.utm_source
  }
  if (params.fbclid && !fbclidDeVerdade(params.fbclid)) delete params.fbclid

  // Fallback: cookies wt_* do dominio raiz (first-touch da landing). So entram
  // se a URL nao trouxe nenhum identificador de campanha, para nao
  // sobrescrever um clique novo com um antigo.
  const urlTemCampanha = !!(params.utm_source || params.gclid || params.gbraid || params.wbraid || params.fbclid)
  if (!urlTemCampanha) {
    for (const key of COOKIE_FALLBACK_KEYS) {
      /* O `wt_fbclid` também nasce do `captureFbclid` deste app, que até
         27/10/2026 gravava ali o id da UTMify: lido cru, ele virava
         `facebook / cpc` logo abaixo. */
      const cru = lerCookieWt(key)
      const val = key === "utm_source" ? limparUtmSource(cru) : key === "fbclid" ? fbclidDeVerdade(cru) : cru
      if (val) params[key] = val
    }
  } else {
    // Mesmo quando a URL traz campanha (ex.: clique no CTA da LP), preserva
    // pagina_de_entrada, referrer_externo e metadados de anuncio dos cookies wt_*
    for (const key of ["cid", "aid", "plac", "site", "pagina_de_entrada", "referrer_externo"] as const) {
      if (!params[key]) {
        const val = lerCookieWt(key)
        if (val) params[key] = val
      }
    }
  }

  // Se nao veio referrer_externo do cookie wt_*, captura document.referrer quando externo
  if (!params.referrer_externo && typeof document !== "undefined" && document.referrer) {
    try {
      const refHost = new URL(document.referrer).hostname.toLowerCase()
      if (refHost && !refHost.endsWith("welltrainer.com.br")) {
        params.referrer_externo = document.referrer
      }
    } catch {}
  }

  // Infer utm_source/utm_medium from click IDs when not explicitly set.
  // `s` (ex.: s=lp) NAO vira utm_source: era isso que rotulava trafego organico
  // e direto da landing como origem "lp" e apagava a origem real no GA4 e no
  // banco. Sem identificador, o backend classifica como DIRETO.
  if (!params.utm_source) {
    if (params.gclid || params.gbraid || params.wbraid) {
      params.utm_source = "google"
      params.utm_medium = params.utm_medium || "cpc"
    } else if (params.fbclid) {
      params.utm_source = "facebook"
      params.utm_medium = params.utm_medium || "cpc"
    }
  }

  // Only overwrite if we found at least one param
  if (Object.keys(params).length > 0) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(params))
  }
}

/**
 * Get stored tracking params as a plain object.
 */
export function getStoredUtmParams(): Record<string, string> {
  if (typeof window === "undefined") return {}

  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? semFbclidDaUtmify(JSON.parse(raw)) : {}
  } catch {
    return {}
  }
}

/**
 * Append stored UTM params to a path/URL string.
 * Preserves any existing query params on the path.
 *
 * Example:
 *   buildUrlWithUtm("/login")  => "/login?utm_source=google&utm_medium=cpc"
 *   buildUrlWithUtm("/criar-senha?email=a@b.com") => "/criar-senha?email=a@b.com&utm_source=google"
 */
export function buildUrlWithUtm(path: string): string {
  const stored = getStoredUtmParams()
  const filtrado: Record<string, string> = {}
  for (const [k, v] of Object.entries(stored)) {
    if (k === "pagina_de_entrada" || k === "referrer_externo") continue
    if (v) filtrado[k] = v
  }
  if (Object.keys(filtrado).length === 0) return path

  const separator = path.includes("?") ? "&" : "?"
  const utmString = new URLSearchParams(filtrado).toString()
  return `${path}${separator}${utmString}`
}

/**
 * Push gclid to dataLayer so GTM Conversion Linker can pick it up.
 * Also ensures _gcl_aw cookie gets set on the app domain.
 * Call on pages that receive traffic from Google Ads (signup, go, login).
 */
export function pushGclidToDataLayer(): void {
  if (typeof window === "undefined") return

  const url = new URL(window.location.href)
  const gclidFromUrl = url.searchParams.get("gclid")
  const stored = getStoredUtmParams()
  const gclid = gclidFromUrl || stored.gclid

  if (!gclid) return

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: "gclid_available",
    gclid,
  })
}

/**
 * Get the stored gclid (from URL or localStorage).
 */
export function getGclid(): string | null {
  if (typeof window === "undefined") return null

  const url = new URL(window.location.href)
  const gclidFromUrl = url.searchParams.get("gclid")
  if (gclidFromUrl) return gclidFromUrl

  const stored = getStoredUtmParams()
  return stored.gclid || null
}
